import { useNavigate } from "react-router-dom";

import { trackEvent } from "@lib/infrastructure";
import {
  getHaNSelectionNodeId,
  HAN_SEARCH_TAB_BY_KIND,
  HaNContext,
  HaNSearchContext,
  type HaNSearchEntry,
  type HaNSearchResult,
  type HaNSelection,
  HaNViewStoreContext,
} from "@lib/logic";
import { useRequiredContext } from "@lib/utils";

import { useHaNTabLinkSearch } from "../data/useHaNBuilding";
import { useConfirmDiscardingBooking } from "../details/useConfirmDiscardingBooking";
import { useFitNodeInView } from "../map/useFitNodeInView";
import { useHaNLayoutMode } from "../state/layout/useHaNLayoutMode";

const toSelection = (entry: HaNSearchEntry): HaNSelection => {
  if (entry.kind === "person") {
    return { kind: "person", id: entry.id, nodeId: entry.reservation?.workspace.nodeId ?? null };
  }

  return { kind: entry.kind, id: entry.id };
};

const getFloorId = (entry: HaNSearchEntry) => {
  if (entry.kind === "person") return entry.reservation?.workspace.floorId ?? null;

  return entry.resource.floorId;
};

type ResultListPlace = { position: number; resultCount: number; isExpanded: boolean };

/**
 * Opens a search result's details on its tab and floor. The results stay, to open another one, except on phones,
 * where they cover the map. A person who isn't in the building today opens without a place on the map. Asks first when
 * that would throw away a booking with changes.
 */
export const useOpenSearchResult = () => {
  const navigate = useNavigate();
  const tabLinkSearch = useHaNTabLinkSearch();
  const layoutMode = useHaNLayoutMode();
  const { tab, building, floor } = useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const { settledQuery, setQuery, rememberPerson } = useRequiredContext(HaNSearchContext);
  const confirmDiscardingBooking = useConfirmDiscardingBooking();
  const fitNodeInView = useFitNodeInView();

  return (result: HaNSearchResult, listPlace: ResultListPlace) => {
    const { entry } = result;
    const resultTab = HAN_SEARCH_TAB_BY_KIND[entry.kind];
    const selection = toSelection(entry);
    const floorId = getFloorId(entry);

    confirmDiscardingBooking(() => {
      const isOnShownFloor = !floorId || floorId === floor.floorId;

      if (!isOnShownFloor) floor.selectFloor(floorId);

      // The building's people don't have them, so their details find them here
      if (entry.kind === "person" && !entry.reservation) rememberPerson(entry.person);

      viewStore.select(resultTab, selection, { openDetails: true });

      if (layoutMode === "mobile") setQuery("");

      if (resultTab !== tab) {
        navigate({ pathname: `/now/${resultTab}`, search: tabLinkSearch });
      }

      const nodeId = getHaNSelectionNodeId(selection);

      if (isOnShownFloor && nodeId) fitNodeInView(nodeId, { evenIfInView: true });

      trackEvent("HereAndNow_Search__ResultClick", {
        buildingId: building.buildingId ?? "",
        tab,
        kind: entry.kind,
        match: result.match,
        isInBuilding: entry.kind !== "person" || entry.reservation !== null,
        isOnOtherFloor: !isOnShownFloor,
        queryLength: settledQuery.length,
        ...listPlace,
      });
    }, selection.id);
  };
};
